import { Observable, Subject } from 'rxjs';

import {
    Component, ComponentFactoryResolver, ElementRef, EventEmitter,
    HostListener, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation
} from '@angular/core';

import { ComponentType, ModalConfig } from '../services/modal.service';

@Component({
    selector: 'odd-modal',
    templateUrl: './modal.component.html',
    styleUrls: [ './modal.component.scss', ],
    encapsulation: ViewEncapsulation.None,
})
export class ModalComponent<T> implements OnInit {
    @ViewChild('header', { static: false }) public readonly headerEl: ElementRef;
    @ViewChild('footer', { static: false }) public readonly footerEl: ElementRef;
    @ViewChild('okButton', { static: false }) public readonly okButtonEl: ElementRef;
    @ViewChild('cancelButton', { static: false }) public readonly cancelButtonEl: ElementRef;
    @ViewChild('backdropButton', { static: false }) public readonly backdropButtonEl: ElementRef;
    @ViewChild('componentSection', { read: ViewContainerRef, static: true }) public readonly componentSection: ViewContainerRef;

    public open = false;
    public componentRef: T;
    public isFull: boolean;
    public isSmall: boolean;
    public isLarge: boolean;
    public config: ModalConfig;
    public hasComponent = false;
    public componentInstance: T;
    public ready = new EventEmitter<boolean>();

    private fromOkButton = false;
    private afterClosed$: Subject<any> = new Subject();

    constructor(
        private readonly el: ElementRef,
        private readonly componentFactoryResolver: ComponentFactoryResolver
    ) {}

    public ngOnInit() {
        this.open = true;
        this.ready.next(true);
        this.isSmall = this.config.isSmall;
        this.isLarge = this.config.isLarge;
        this.isFull = this.config.isFull;
    }

    @HostListener('document:keydown.escape')
    public keypress() {
        if (this.config.hasBackdrop) {
            this.dismiss(false);
        }
    }

    public backdropHandler() {
        if (this.config.hasBackdrop) {
            this.dismiss(false);
        }
    }

    public cancelHandler() {
        if (this.config.cancelHandler && typeof this.config.cancelHandler === 'function') {
            this.config.cancelHandler();
        } else {
            this.dismiss(false);
        }
    }

    public okHandler() {
        if (this.config.okHandler && typeof this.config.okHandler === 'function') {
            this.config.okHandler();
        } else {
            this.dismiss(true);
        }
    }

    public disableButtonsAndMakeOkButtonLoading() {
        if (this.cancelButtonEl) {
            this.cancelButtonEl.nativeElement.setAttribute('disabled', 'disabled');
        }

        if (this.okButtonEl) {
            this.okButtonEl.nativeElement.setAttribute('disabled', 'disabled');
            this.okButtonEl.nativeElement.classList.add('is-loading');
        }

        if (this.backdropButtonEl) {
            this.backdropButtonEl.nativeElement.setAttribute('disabled', 'disabled');
        }
    }

    public enableButtonsAndMakeOkButtonNotLoading() {
        if (this.cancelButtonEl) {
            this.cancelButtonEl.nativeElement.removeAttribute('disabled');
        }

        if (this.okButtonEl) {
            this.okButtonEl.nativeElement.removeAttribute('disabled');
            this.okButtonEl.nativeElement.classList.remove('is-loading');
        }

        if (this.backdropButtonEl) {
            this.backdropButtonEl.nativeElement.removeAttribute('disabled');
        }
    }

    public dismiss(fromOkButton: boolean) {
        this.open = false;

        this.el.nativeElement.remove();

        this.fromOkButton = fromOkButton;
    }

    public afterClosed(): Observable<boolean> {
        return this.afterClosed$.asObservable();
    }

    public _setComponent(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>) {
        let factory;

        if (componentOrTemplateRef instanceof TemplateRef) {
            this.componentSection.createEmbeddedView(componentOrTemplateRef);
        } else {
            factory = this.componentFactoryResolver.resolveComponentFactory(componentOrTemplateRef);
            const componentRef = this.componentSection.createComponent(factory);

            this.componentInstance = componentRef.instance as T;

            (this.componentInstance as any).el = this.el;
            (this.componentInstance as any).dismiss = this.dismiss.bind(this);
        }
        this.hasComponent = true;
    }
}
